/**
 * src/integrations/api/auth.ts
 * Auth helpers — replaces Supabase auth.signInWithPassword / getUser / signOut
 */
import { apiPost, apiGet, tokenStore, getUserServiceUrl } from "./client";
import { isIntegratedMode, tenantPortalLoginUrl } from "./sso";
import type { AppRole } from "@/lib/roles";

export type MeResult = {
  user: { id: string; email: string | null };
  profile: {
    id: string;
    full_name: string | null;
    email: string | null;
    avatar_url?: string | null;
    branch_id: string | null;
    department_id: string | null;
    is_active: boolean;
  } | null;
  roles: AppRole[];
  permissions: string[];
  branches: { id: string; name: string; code: string }[];
  hod_department_ids: string[];  // raw snake_case from API
  hodDepartmentIds: string[];    // normalized camelCase alias — use this in components
  must_change_password?: boolean;
};

export async function signIn(email: string, password: string, organisationCode?: string): Promise<void> {
  const payload: Record<string, string> = { email, password };
  if (organisationCode) payload.organisation_code = organisationCode;
  const data = await apiPost<{ access: string; refresh: string }>(
    "/api/auth/login/",
    payload
  );
  tokenStore.set(data.access, data.refresh);
}

export async function signOut(options?: { localOnly?: boolean }): Promise<void> {
  const refresh = tokenStore.getRefresh();
  const access = tokenStore.getAccess();
  tokenStore.clear();

  if (isIntegratedMode() && !options?.localOnly) {
    const url = new URL(tenantPortalLoginUrl());
    url.searchParams.set("logged_out", "1");
    window.location.replace(url.toString());
    const payload = JSON.stringify({ refresh, access });
    try {
      void fetch(getUserServiceUrl("/api/platform/auth/logout/"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: payload,
        keepalive: true,
      });
    } catch {
      /* unloading */
    }
    if (refresh) {
      try {
        void fetch("/api/auth/logout/", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ refresh }),
          keepalive: true,
        });
      } catch {
        /* unloading */
      }
    }
    return;
  }

  if (refresh) {
    try {
      await apiPost("/api/auth/logout/", { refresh });
    } catch {
      /* ignore — still clear tokens */
    }
  }
}

import { useAuthStore as usePermStore } from "@/store/authStore";
import { useAuthStore } from "@/store/useAuthStore";

export async function getMe(): Promise<MeResult> {
  const raw = await apiGet<any>("/api/auth/me/");
  const hodIds = (raw.hod_department_ids ?? []).map(String);
  // Backend nests must_change_password inside the `user` object:
  // { user: { id, email, must_change_password }, profile, roles, ... }
  const mustChangePwd = raw.user?.must_change_password ?? raw.must_change_password ?? false;
  const me = {
    ...raw,
    hod_department_ids: hodIds,
    hodDepartmentIds: hodIds,
    must_change_password: mustChangePwd,
  };
  usePermStore.getState().setPermissions(me.permissions ?? []);
  return me;
}

export function isAuthenticated(): boolean {
  return !!tokenStore.getAccess();
}

export async function requestPasswordReset(email: string): Promise<void> {
  await apiPost("/api/auth/password-reset/", { email });
}

export async function changePassword(new_password: string, confirm_password: string): Promise<MeResult> {
  const data = await apiPost<{ access: string; refresh: string }>("/api/auth/change-password/", { new_password, confirm_password });
  if (data.access && data.refresh) {
    tokenStore.set(data.access, data.refresh);
    // Force refresh of the me profile
    const me = await getMe();
    useAuthStore.getState().setAuth(me);
    return me;
  }
  throw new Error("Failed to update password");
}
